// PayoutTab.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import Token from "../../../util/Token";
import { getTeamPayout } from "src/react-query/endPoints";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useDomainConfig } from "src/hooks/useDomainConfig";

export const PayoutsTab = () => {
  const { teamId } = useSelectedTeam();
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [payoutData, setPayoutData] = useState(null);
  const [payoutError, setPayoutError] = useState(null);
  const [loading, setLoading] = useState(false);
  const domainConfig = useDomainConfig();

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // 1-indexed (1=Jan, 12=Dec)

  const yearOptions = useMemo(() => {
    const years = [];
    for (let year = 2020; year <= currentYear; year++) {
      years.push(year);
    }
    return years;
  }, [currentYear]);

  const monthOptions = useMemo(() => {
    const months = [
      { value: 1, label: "January" },
      { value: 2, label: "February" },
      { value: 3, label: "March" },
      { value: 4, label: "April" },
      { value: 5, label: "May" },
      { value: 6, label: "June" },
      { value: 7, label: "July" },
      { value: 8, label: "August" },
      { value: 9, label: "September" },
      { value: 10, label: "October" },
      { value: 11, label: "November" },
      { value: 12, label: "December" },
    ];

    if (selectedYear === currentYear) {
      return months.filter((m) => m.value < currentMonth);
    }

    return months;
  }, [selectedYear, currentYear, currentMonth]);

  const handleFetchPayout = async () => {
    if (!selectedYear || selectedMonth === "") {
      alert("Please select both month and year to view payout data");
      return;
    }

    try {
      setLoading(true);
      setPayoutError(null);
      const token = Token.get();

      const response = await axios.get(
        getTeamPayout(
          typeof teamId === "object" ? teamId.id : teamId,
          Number(selectedYear),
          Number(selectedMonth)
        ),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPayoutData(response.data);
    } catch (error) {
      console.error("PayoutTab: Error fetching payout data:", {
        error: error.message,
        stack: error.stack,
        teamId: teamId,
        selectedYear: selectedYear,
        selectedMonth: selectedMonth
      });
      setPayoutError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
    setPayoutData(null);
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
    setSelectedMonth("");
    setPayoutData(null);
  };

  const handleDownloadPDF = (teamPayout) => {
    if (!teamPayout?.data) {
      alert("No payout data available to download");
      return;
    }

    const doc = new jsPDF();
    const { teamDetails, summary, chargerSummary } = teamPayout.data;
    const acc = teamDetails.payoutAccounts?.[0];

    const selectedMonthLabel =
      monthOptions.find((m) => m.value === selectedMonth)?.label || "";
    const invoicePeriod = `${selectedMonthLabel} ${selectedYear}`;

    doc.setFontSize(16);
    doc.text("Payout Invoice", 14, 20);

    doc.setFontSize(11);
    let y = 40;
    doc.text(`Beneficiary: ${acc?.beneficiary_name || "-"}`, 14, y);
    doc.text(`Business: ${acc?.business_name || "-"}`, 14, y + 6);
    doc.text(`Business Type: ${acc?.business_type || "-"}`, 14, y + 12);
    doc.text(`Phone: ${acc?.phone || "-"}`, 14, y + 18);
    doc.text(`Email: ${acc?.email || "-"}`, 14, y + 24);
    doc.text(`GST: ${acc?.gst || "-"}`, 14, y + 30);

    doc.setFontSize(11);
    let rightX = 140;
    doc.text(
      `Invoice Date: ${new Date().toLocaleDateString("en-IN")}`,
      rightX,
      y
    );
    doc.text(`Team: ${teamDetails?.name || "-"}`, rightX, y + 6);
    doc.text(`Period: ${invoicePeriod}`, rightX, y + 12);

    autoTable(doc, {
      startY: 85,
      head: [
        [
          "Transactions",
          "Chargers",
          "kWh",
          "Total Collection",
          `${domainConfig.name} Charges`,
          "Total Payout",
        ],
      ],
      body: [
        [
          summary.totalTransactions || 0,
          summary.totalChargers || 0,
          summary.totalKW ? summary.totalKW.toFixed(3) : '0.000',
          `Rs ${summary.totalCollectionWithoutPGC ? summary.totalCollectionWithoutPGC.toFixed(2) : '0.00'}`,
          `Rs ${summary.totalAmikServiceCharges ? summary.totalAmikServiceCharges.toFixed(2) : '0.00'}`,
          `Rs ${summary.totalPayoutAmount ? summary.totalPayoutAmount.toFixed(2) : '0.00'}`,
        ],
      ],
    });

    const chargerRows = Object.entries(chargerSummary).map(([id, c]) => [
      id,
      c.charger_type || '',
      (c.totalKW ? c.totalKW.toFixed(3) : '0.000') + " kWh",
      `Rs ${c.totalCollectionWithoutPGC ? c.totalCollectionWithoutPGC.toFixed(2) : '0.00'}`,
      `Rs ${c.maxAmikServiceCharges ? c.maxAmikServiceCharges.toFixed(2) : '0.00'}`,
    ]);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [
        ["Charger ID", "Type", "kWh", "Collection (Rs)", `${domainConfig.name} Charges (Rs)`],
      ],
      body: chargerRows,
    });

    doc.save(`Payout_Invoice_${teamDetails.name}_${invoicePeriod}.pdf`);
  };

  const handleDownloadExcel = () => {
    if (!payoutData?.data || !payoutData.data.length) {
      alert("No payout data available to download");
      return;
    }

    // Prepare data for Excel
    const excelData = payoutData.data.map((item) => {
      const teamPayout = item.payout;
      const teamDetails = teamPayout.data.teamDetails;
      const summary = teamPayout.data.summary;
      const account = teamDetails.payoutAccounts?.[0] || {};

      return {
        "Beneficiary Name": account.beneficiary_name || "",
        "Beneficiary Email ID": account.email || "",
        "Beneficiary Account Number": account.account_no || "",
        IFSC: account.ifsc_code || "",
        "Transaction Type": "NEFT",
        "Debit Account Number": "",
        "Transaction Date": new Date().toLocaleDateString("en-IN"),
        Amount: summary.totalPayoutAmount ? summary.totalPayoutAmount.toFixed(2) : '0.00',
        Currency: "INR",
        Remarks: `Payout for ${teamDetails.name} - ${
          monthOptions.find((m) => m.value === selectedMonth)?.label
        } ${selectedYear}`,
      };
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payouts");

    // Generate Excel file and trigger download
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Payouts_${selectedMonth}_${selectedYear}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box>
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Payout Management
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel id="year-select-label">Year</InputLabel>
            <Select
              labelId="year-select-label"
              value={selectedYear}
              label="Year"
              onChange={handleYearChange}
            >
              {yearOptions.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120 }} disabled={!selectedYear}>
            <InputLabel id="month-select-label">Month</InputLabel>
            <Select
              labelId="month-select-label"
              value={selectedMonth}
              label="Month"
              onChange={handleMonthChange}
            >
              {monthOptions.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleFetchPayout}
            disabled={!selectedMonth || !selectedYear || loading}
          >
            {loading ? <CircularProgress size={24} /> : "Fetch Payout Data"}
          </Button>

          {payoutData?.data && payoutData.data.length > 0 && (
            <Button
              variant="outlined"
              color="success"
              onClick={handleDownloadExcel}
            >
              Download Excel
            </Button>
          )}
        </Stack>

        {payoutError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Unable to load payout data: {payoutError.message || "Please try again later"}
          </Alert>
        )}
      </Card>

      {payoutData?.data && payoutData.data.length > 0 ? (
        <Box>
          <Typography variant="h6" gutterBottom>
            Payout Summary for{" "}
            {monthOptions.find((m) => m.value === selectedMonth)?.label}{" "}
            {selectedYear}
          </Typography>

          {payoutData.data.map((item) => {
            const teamPayout = item.payout;
            const teamDetails = teamPayout.data.teamDetails;
            const summary = teamPayout.data.summary;

            // Safety check for summary object
            if (!summary) {
              return (
                <Card key={item.teamId} sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6">{teamDetails.name}</Typography>
                  <Alert severity="warning">Summary data not available</Alert>
                </Card>
              );
            }

            return (
              <Card key={item.teamId} sx={{ p: 3, mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">{teamDetails.name}</Typography>
                  <Chip
                    label={
                      (summary.totalPayoutAmount || 0) > 0 ? "Payable" : "No Payout"
                    }
                    color={
                      (summary.totalPayoutAmount || 0) > 0 ? "primary" : "default"
                    }
                  />
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      <strong>Total Transactions:</strong>{" "}
                      {summary.totalTransactions || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      <strong>Total Collection:</strong> ₹
                      {summary.totalCollectionWithoutPGC ? summary.totalCollectionWithoutPGC.toFixed(2) : '0.00'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      <strong>{domainConfig.name} Service Charges:</strong> ₹
                      {summary.totalAmikServiceCharges ? summary.totalAmikServiceCharges.toFixed(2) : '0.00'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      <strong>Total Payout:</strong> ₹
                      {summary.totalPayoutAmount ? summary.totalPayoutAmount.toFixed(2) : '0.00'}
                    </Typography>
                  </Grid>
                </Grid>

                {Object.keys(teamPayout.data.chargerSummary).length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                      Charger Breakdown:
                    </Typography>
                    {Object.entries(teamPayout.data.chargerSummary).map(
                      ([chargerId, chargerData]) => (
                        <Box
                          key={chargerId}
                          sx={{
                            mt: 1,
                            p: 1,
                            backgroundColor: "grey.100",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body2">
                            <strong>{chargerId}:</strong> ₹
                            {chargerData.totalCollectionWithoutPGC ? chargerData.totalCollectionWithoutPGC.toFixed(2) : '0.00'} |{" "}
                            {chargerData.totalKW ? chargerData.totalKW.toFixed(3) : '0.000'} kWh |{" "}
                            ₹ {chargerData.maxAmikServiceCharges ? chargerData.maxAmikServiceCharges.toFixed(2) : '0.00'} 
                          </Typography>
                        </Box>
                      )
                    )}
                  </>
                )}

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => handleDownloadPDF(teamPayout)}
                  sx={{ mt: 2 }}
                  disabled={(summary.totalPayoutAmount || 0) === 0}
                >
                  Download PDF
                </Button>
              </Card>
            );
          })}
        </Box>
      ) : payoutData?.data && payoutData.data.length === 0 ? (
        <Alert severity="info">
          No payout data available for the selected period.
        </Alert>
      ) : null}
    </Box>
  );
};
